import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Mail, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Banner } from '../src/components/ui/banner';
import { Input } from '../src/components/ui/input';
import { Button } from '../src/components/ui/button';
import { IconButton } from '../src/components/ui/icon-button';
import { useFocusTrap } from '../src/hooks/useFocusTrap';

interface AuthModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, signInWithGoogle, sendMagicLink, signInWithGoogleRedirect } = useAuth();
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [resendSuccess, setResendSuccess] = useState(false);
    const [cooldownSeconds, setCooldownSeconds] = useState(0);
    const [cooldownEmail, setCooldownEmail] = useState<string | null>(null);
    // Transition-only SR announcement (changelog (+11)): latched at phase changes
    // (cooldown start / resend success / cooldown end), never per tick. The ticking
    // button texts below deliberately carry NO aria-live — the old pattern announced
    // every second of the 60s cooldown on some screen readers, from two counters at
    // once. All latched strings are existing on-screen text; none is new copy.
    // Character-matching twin of pages/AuthPage.tsx.
    const [cooldownAnnouncement, setCooldownAnnouncement] = useState('');
    // 'Sending...' is announced ONLY for actual magic-link sends. isLoading is
    // shared with Google sign-in, where a "Sending..." announcement would be
    // false (review RISK) — so the region keys on this dedicated flag, set
    // exclusively by handleEmailSubmit/handleResendLink.
    const [isSendingLink, setIsSendingLink] = useState(false);
    const cooldownIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    // Modal focus management: initial focus, Tab/Shift+Tab trap, Escape, focus restore.
    useFocusTrap({ active: isOpen, containerRef: panelRef, initialFocusRef: closeButtonRef, onEscape: onClose });

    // Auto-close modal when user signs in (handles cross-tab magic link sign-in)
    useEffect(() => {
        if (isOpen && user) {
            onClose();
        }
    }, [isOpen, user, onClose]);

    // Cleanup cooldown interval on unmount
    useEffect(() => {
        return () => {
            if (cooldownIntervalRef.current) {
                clearInterval(cooldownIntervalRef.current);
            }
        };
    }, []);

    // The isOpen gate removes the DOM but the component INSTANCE persists (Header
    // mounts the modal unconditionally), so without this a reopen would re-insert
    // the live region pre-loaded with the stale latch — which some SR/browser
    // pairs announce (the UI-31 Escape-reopen class; review RISK). Clearing on
    // close makes the region re-enter genuinely empty; the interval's zero-branch
    // is additionally gated on isOpenRef below so a cooldown expiring WHILE
    // CLOSED cannot re-populate the latch before a reopen.
    useEffect(() => {
        if (!isOpen) setCooldownAnnouncement('');
    }, [isOpen]);
    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;

    // Single source for the cooldown length — the timer and the one-shot start
    // announcement must agree.
    const RESEND_COOLDOWN_SECONDS = 60;

    const startCooldownTimer = (emailToTrack: string, announcement: string) => {
        setCooldownEmail(emailToTrack.toLowerCase());
        setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
        // Latch only while OPEN (re-verify catch): a send resolving AFTER the user
        // closed the modal would otherwise re-populate the latch the close-effect
        // just cleared, and ride the next reopen's DOM insertion as a replay.
        // Cooldown STATE writes above stay unconditional — only the announcement
        // is presence-gated. (AuthPage needs no twin: page unmount destroys state.)
        if (isOpenRef.current) setCooldownAnnouncement(announcement);
        if (cooldownIntervalRef.current) {
            clearInterval(cooldownIntervalRef.current);
        }
        cooldownIntervalRef.current = setInterval(() => {
            setCooldownSeconds(prev => {
                if (prev <= 1) {
                    if (cooldownIntervalRef.current) {
                        clearInterval(cooldownIntervalRef.current);
                        cooldownIntervalRef.current = null;
                    }
                    setCooldownEmail(null);
                    // "You can resend now" — announced with the button's own
                    // restored label, the wait-over transition the region exists
                    // for. Only while the modal is OPEN: a latch written while
                    // closed would ride the next reopen's DOM insertion as a
                    // spurious announcement on some SRs.
                    if (isOpenRef.current) setCooldownAnnouncement('Resend link');
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    if (!isOpen) return null;

    // Recognised authenticated routes (no UID prefix needed — auth guard handles protection)
    const authenticatedPaths = ['/account', '/favourites'];

    const handleGoogleSignIn = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const signedInUser = await signInWithGoogle();
            // After successful sign-in, navigate to the current path if it's a recognised
            // authenticated route; otherwise stay put. No UID is added to the URL.
            if (signedInUser) {
                const currentPath = location.pathname;
                const isAuthRoute = authenticatedPaths.some(p => currentPath === p || currentPath.startsWith(p + '/'));
                if (isAuthRoute) {
                    navigate(currentPath, { replace: true });
                }
            }
            onClose();
        } catch (err: any) {
            console.error('[AuthModal] Google Sign-in error:', err);
            if (err.code === 'auth/popup-blocked') {
                setError('Sign-in popup was blocked. Please allow popups or use the link below.');
            } else if (err.code === 'auth/popup-closed-by-user') {
                setError('Sign-in cancelled. Please keep the window open.');
            } else {
                // Ensure error code is visible
                const code = err.code || err.message || 'unknown-error';
                setError(`Failed to sign in (${code}). Please try again or use the redirect option.`);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleSignInRedirect = () => {
        signInWithGoogleRedirect();
    };

    const handleEmailSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim()) return;

        // Check if this email is under cooldown
        if (cooldownEmail === email.toLowerCase() && cooldownSeconds > 0) {
            setError(`Please wait ${cooldownSeconds} seconds before requesting another link.`);
            return;
        }

        setIsLoading(true);
        setIsSendingLink(true);
        setError(null);
        // Clear the latch BEFORE the send: on failure the region then collapses
        // silently ('Sending...' → '', which live regions do not announce) and the
        // role="alert" Banner alone carries the error — a failed send must never
        // replay a stale 'Link sent!' / 'Resend in 60s' (review REAL-BUG).
        setCooldownAnnouncement('');
        try {
            await sendMagicLink(email);
            setEmailSent(true);
            startCooldownTimer(email, `Resend in ${RESEND_COOLDOWN_SECONDS}s`);
        } catch (err) {
            setError('Failed to send sign-in link. Please try again.');
        } finally {
            setIsLoading(false);
            setIsSendingLink(false);
        }
    };

    const handleResendLink = async () => {
        if (cooldownSeconds > 0) return;

        setIsLoading(true);
        setIsSendingLink(true);
        setError(null);
        setCooldownAnnouncement('');
        setResendSuccess(false);
        try {
            await sendMagicLink(email);
            setResendSuccess(true);
            startCooldownTimer(email, 'Link sent!');
            setTimeout(() => setResendSuccess(false), 3000);
        } catch (err) {
            setError('Failed to resend sign-in link. Please try again.');
        } finally {
            setIsLoading(false);
            setIsSendingLink(false);
        }
    };

    const handleChangeEmail = () => {
        setEmailSent(false);
        setError(null);
        setResendSuccess(false);
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm modal-backdrop"
            onClick={handleBackdropClick}
        >
            {/* UI-05: max-h + internal scroll (FilterModal's proven pattern) — without
                them, a viewport shorter than the panel put the heading and the submit
                button off-screen with no way to reach them. The close button stays
                outside the scroll region so it never scrolls away. */}
            <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby="auth-modal-title" className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
                {/* Persistent transition-only live region (changelog (+11)); sits
                    OUTSIDE the emailSent branches so screen switches never remount
                    it. NOTE the isOpen gate removes only the DOM — the component
                    instance and its state persist across close/reopen — so the
                    UI-31 Escape-reopen replay class is prevented EXPLICITLY: the
                    latch clears on close and the interval's zero-branch is gated
                    on isOpenRef, guaranteeing the region re-inserts empty.
                    'Sending...' overlays during an actual link send (isSendingLink,
                    NOT the shared isLoading — the Google flow must not announce
                    it); latched phases otherwise. */}
                <div className="sr-only" aria-live="polite">
                    {isSendingLink ? 'Sending...' : cooldownAnnouncement}
                </div>
                {/* Close Button */}
                <IconButton
                    ref={closeButtonRef}
                    onClick={onClose}
                    ariaLabel="Close sign-in dialog"
                    variant="ghost"
                    size="dense"
                    icon={<X size={20} aria-hidden="true" />}
                    className="absolute top-4 right-4 z-10 border-0 bg-white/90 hover:bg-white/90 text-text-subtle hover:text-text-primary [&>svg]:w-5 [&>svg]:h-5"
                />

                <div className="p-8 flex-1 min-h-0 overflow-y-auto">
                    {emailSent ? (
                        // Email Sent - Check Your Email State
                        <div className="bg-white">
                            <div className="text-center mb-6">
                                <h2 id="auth-modal-title" className="text-2xl font-bold text-brand-primary mb-4">Check Your Email</h2>
                                <p className="text-text-primary leading-relaxed">
                                    We've sent a secure sign-in link to
                                </p>
                                <p className="font-semibold text-text-primary mb-6">
                                    {email}
                                </p>
                                <p className="text-text-primary leading-relaxed">
                                    After clicking the link you'll be signed in automatically
                                </p>
                            </div>

                            {/* Action Links */}
                            <div className="flex items-center justify-center gap-6 mb-6">
                                <button
                                    onClick={handleResendLink}
                                    disabled={isLoading || cooldownSeconds > 0}
                                    className="text-brand-primary text-sm font-medium hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isLoading ? 'Sending...' : resendSuccess ? 'Link sent!' : cooldownSeconds > 0 ? `Resend in ${cooldownSeconds}s` : 'Resend link'}
                                </button>
                                <button
                                    onClick={handleChangeEmail}
                                    className="text-brand-primary text-sm font-medium hover:underline"
                                >
                                    Change email address
                                </button>
                            </div>

                            {error && (
                                <Banner variant="danger" className="mb-4">
                                    {error}
                                </Banner>
                            )}

                            {/* Help Section */}
                            <div className="bg-surface-canvas rounded-xl p-4">
                                <p className="text-text-muted text-sm mb-3 leading-relaxed">Can't find the email?</p>
                                <ul className="text-text-muted text-sm">
                                    <li className="flex items-start gap-2.5">
                                        <span aria-hidden="true" className="mt-[0.6em] w-1.5 h-1.5 bg-text-muted rounded-full flex-shrink-0" />
                                        <span className="leading-relaxed">Make sure the email address is correct</span>
                                    </li>
                                    <li className="flex items-start gap-2.5">
                                        <span aria-hidden="true" className="mt-[0.6em] w-1.5 h-1.5 bg-text-muted rounded-full flex-shrink-0" />
                                        <span className="leading-relaxed">Delivery may take up to 2 minutes</span>
                                    </li>
                                    <li className="flex items-start gap-2.5">
                                        <span aria-hidden="true" className="mt-[0.6em] w-1.5 h-1.5 bg-text-muted rounded-full flex-shrink-0" />
                                        <span className="leading-relaxed">Check your spam or promotions folder</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    ) : (
                        // Sign In Form
                        <div className="bg-white">
                            <div className="text-center mb-8">
                                <h2 id="auth-modal-title" className="text-2xl font-bold text-brand-primary mb-3">Begin Your Martial Arts Journey</h2>
                                <p className="text-text-muted text-sm leading-relaxed">
                                    Create an account to save gyms and searches
                                </p>
                            </div>

                            {error && (
                                <Banner variant="danger" className="mb-6">
                                    {error}
                                </Banner>
                            )}

                            {/* Google Sign In */}
                            <Button
                                variant="secondary"
                                fullWidth
                                onClick={handleGoogleSignIn}
                                disabled={isLoading}
                                className="flex gap-3 py-3 text-base rounded-xl disabled:pointer-events-auto disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                                            <path
                                                fill="#4285F4"
                                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                            />
                                            <path
                                                fill="#34A853"
                                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                            />
                                            <path
                                                fill="#FBBC05"
                                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                            />
                                            <path
                                                fill="#EA4335"
                                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                            />
                                        </svg>
                                        Sign in with Google
                                    </>
                                )}
                            </Button>

                            {/* Redirect Fallback */}
                            {!emailSent && (
                                <div className="mt-3 text-center">
                                    <button
                                        onClick={handleGoogleSignInRedirect}
                                        className="text-xs text-text-muted hover:text-brand-primary hover:underline underline-offset-4"
                                    >
                                        Popup not working? Try redirected sign-in
                                    </button>
                                </div>
                            )}

                            {/* Divider */}
                            <div className="flex items-center gap-4 my-6">
                                <div className="flex-1 h-px bg-border-default" />
                                <span className="text-sm text-text-muted">or</span>
                                <div className="flex-1 h-px bg-border-default" />
                            </div>

                            {/* Email Sign In */}
                            <form onSubmit={handleEmailSubmit}>
                                <label htmlFor="auth-modal-email" className="block text-sm font-medium text-text-primary mb-2">
                                    Email address
                                </label>
                                <Input
                                    id="auth-modal-email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Email address"
                                    disabled={isLoading}
                                />
                                <Button
                                    type="submit"
                                    variant="primary"
                                    fullWidth
                                    disabled={isLoading || !email.trim() || (cooldownEmail === email.toLowerCase() && cooldownSeconds > 0)}
                                    className={`mt-4 flex py-3 text-base rounded-xl border-0 disabled:pointer-events-auto disabled:opacity-100 disabled:cursor-not-allowed ${/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) && !(cooldownEmail === email.toLowerCase() && cooldownSeconds > 0)
                                        ? 'bg-brand-primary hover:bg-brand-primary-hover'
                                        : 'bg-brand-primary-disabled hover:bg-brand-primary-disabled-hover'
                                        }`}
                                >
                                    {isLoading ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : cooldownEmail === email.toLowerCase() && cooldownSeconds > 0 ? (
                                        <>
                                            <Mail className="w-5 h-5" />
                                            Wait {cooldownSeconds}s
                                        </>
                                    ) : (
                                        <>
                                            <Mail className="w-5 h-5" />
                                            Send sign-in link
                                        </>
                                    )}
                                </Button>
                            </form>

                            <p className="mt-6 text-sm text-center text-text-muted">
                                We'll send you a magic link to sign in. No password required.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div >
    );
};

export default AuthModal;
